import { SITE_URL, site } from '@/data/site'
import { getService } from '@/data/services'
import { getPost } from '@/data/blog'
import { getDoctor } from '@/data/doctors'

export interface SeoEntry {
  title: string
  description: string
  /** Open Graph image URL */
  image?: string
  /** Open Graph type */
  type?: string
  /** Set true to keep out of search indexes (auth / 404 pages) */
  noIndex?: boolean
  /** Canonical URL override. Falls back to the current route URL. */
  canonical?: string
}

export const defaultSeo: SeoEntry = {
  title: `${site.name} — ${site.tagline} in Chauddagram`,
  description:
    'Fast Aid Hospital, Chauddagram offers 24/7 emergency care, experienced doctors, diagnostic laboratory and imaging, and affordable health checkups in Chittagong, Bangladesh. Book an appointment on 09610009620.',
  image: `${SITE_URL}/assets/images/hero/bg-hero-3.jpg`,
  type: 'website',
}

export const seoByPath: Record<string, SeoEntry> = {
  '/': {
    title: `${site.name} — 24/7 Hospital in Chauddagram, Chittagong`,
    description:
      'Fast Aid Hospital, Chauddagram provides 24/7 emergency care, general medicine, cardiology, orthopedics, pediatrics and diagnostic services. Book an appointment with our experienced doctors today.',
  },
  '/about': {
    title: `About Us | ${site.name}, Chauddagram`,
    description:
      'Learn about Fast Aid Hospital, Chauddagram — our mission, experienced medical team, facilities and commitment to quality, affordable healthcare for the people of Chittagong.',
  },
  '/services': {
    title: `Our Services | ${site.name}, Chauddagram`,
    description:
      'Explore the medical services at Fast Aid Hospital — 24/7 emergency and trauma care, general medicine, cardiology, orthopedics, pediatrics and diagnostics in Chauddagram.',
  },
  '/services-details': {
    title: `Service Details | ${site.name}`,
    description:
      'Detailed information about a Fast Aid Hospital medical service, including our facilities, treatment process and patient care.',
  },
  '/appointment': {
    title: `Book an Appointment | ${site.name}, Chauddagram`,
    description:
      'Book an appointment online with a Fast Aid Hospital doctor in Chauddagram. Choose your department, doctor, date and time — quick, simple and secure.',
  },
  '/doctors': {
    title: `Our Doctors | ${site.name}, Chauddagram`,
    description:
      'Meet the experienced doctors and specialists at Fast Aid Hospital, Chauddagram — cardiology, neurology, orthopedics, pediatrics, general medicine and more.',
  },
  '/doctor-details': {
    title: `Doctor Profile | ${site.name}`,
    description:
      'View a Fast Aid Hospital doctor profile — specialties, qualifications, experience and consultation schedule.',
  },
  '/portfolio': {
    title: `Our Work | ${site.name}`,
    description:
      'A showcase of the health camps, awareness programmes and community work delivered by Fast Aid Hospital, Chauddagram.',
  },
  '/portfolio-details': {
    title: `Project Details | ${site.name}`,
    description: 'Case study and project details from Fast Aid Hospital, Chauddagram.',
  },
  '/image-gallery': {
    title: `Image Gallery | ${site.name}`,
    description:
      'Photos of Fast Aid Hospital facilities, doctors, events and community health programmes in Chauddagram.',
  },
  '/video-gallery': {
    title: `Video Gallery | ${site.name}`,
    description:
      'Watch videos from Fast Aid Hospital — facilities, patient stories and health awareness from our team in Chauddagram.',
  },
  '/testimonials': {
    title: `Patient Testimonials | ${site.name}`,
    description:
      'Read what patients say about their experience at Fast Aid Hospital, Chauddagram — emergency care, outdoor consultation, diagnostics and more.',
  },
  '/faq': {
    title: `Frequently Asked Questions | ${site.name}`,
    description:
      'Answers to common questions about appointments, visiting hours, emergency care, diagnostic reports and billing at Fast Aid Hospital, Chauddagram.',
  },
  '/error': {
    title: `404 — Page Not Found | ${site.name}`,
    description: 'The page you are looking for could not be found.',
    noIndex: true,
  },
  '/sign-in': {
    title: `Sign In | ${site.name}`,
    description: 'Sign in to your Fast Aid Hospital patient portal account.',
    noIndex: true,
  },
  '/register': {
    title: `Register | ${site.name}`,
    description: 'Create a Fast Aid Hospital patient portal account.',
    noIndex: true,
  },
  '/forget-password': {
    title: `Forgot Password | ${site.name}`,
    description: 'Reset your Fast Aid Hospital patient portal password.',
    noIndex: true,
  },
  '/blog': {
    title: `Health Blog | ${site.name}, Chauddagram`,
    description:
      'Health tips, hospital news and medical advice from the doctors at Fast Aid Hospital, Chauddagram — emergency care, heart health, diabetes and child care.',
  },
  '/blog-details': {
    title: `Blog Details | ${site.name}`,
    description: 'Read the full article from the Fast Aid Hospital health blog.',
  },
  '/contact': {
    title: `Contact Us | ${site.name}, Chauddagram`,
    description:
      'Get in touch with Fast Aid Hospital, Chauddagram — hotline 09610009620, email info@fastaid.com.bd, address and enquiry form.',
  },
  '/terms-and-conditions': {
    title: `Terms and Conditions | ${site.name}`,
    description:
      'Read the terms and conditions for using the Fast Aid Hospital website, online appointments and patient services.',
  },
  '/privacy-policy': {
    title: `Privacy Policy | ${site.name}`,
    description:
      'How Fast Aid Hospital collects, uses and protects your personal and health information.',
  },
}

/**
 * Resolve metadata for a pathname, including dynamic routes such as
 * `/services/:slug`. Returns `{}` when nothing specific is configured so the
 * caller can fall back to `defaultSeo`.
 */
export function resolveSeo(pathname: string): Partial<SeoEntry> {
  const exact = seoByPath[pathname]
  if (exact) return exact

  const serviceMatch = pathname.match(/^\/services\/([^/]+)$/)
  if (serviceMatch) {
    const service = getService(serviceMatch[1])
    if (service) {
      return {
        title: `${service.title} | ${site.name}, Chauddagram`,
        description: service.description,
      }
    }
  }

  const postMatch = pathname.match(/^\/blog\/([^/]+)$/)
  if (postMatch) {
    const post = getPost(postMatch[1])
    if (post) {
      return {
        title: post.metaTitle?.trim() || `${post.title} | ${site.name}`,
        description: post.metaDescription?.trim() || post.excerpt,
      }
    }
  }

  const doctorMatch = pathname.match(/^\/doctors\/([^/]+)$/)
  if (doctorMatch) {
    const doctor = getDoctor(doctorMatch[1])
    if (doctor) {
      return {
        title: `${doctor.name} — ${doctor.specialty} | ${site.name}`,
        description: `Meet ${doctor.name}, ${doctor.specialty} specialist at ${site.name}, Chauddagram. ${doctor.experience} of experience.`,
      }
    }
  }

  return {}
}
